// WebSocketClient.ts

import { store } from "@app/store";
import { addAlert } from "@features/alert/slice";

// type OnOpenCallback = (event: Event) => void;
// type OnMessageCallback = (event: MessageEvent) => void;
// type OnErrorCallback = (event: Event) => void;
// type OnCloseCallback = (event: CloseEvent) => void;

export enum WebsocketUpdateType {
  Order = "order",
  Balance = "balance",
  Requisite = "requisite",
  Transaction = "transaction",
  Notification = "notification",
  Withdrawal = "withdrawal",
  Appeal = "appeal",
  PayOut = "payout",
  General = "general",
}

export interface WebsocketData {}

export interface WebsocketOrderData extends WebsocketData {
  order_id: string;
  status: string;
}
export interface WebsocketBalanceData extends WebsocketData {
  balance: number;
}
export interface WebsocketRequisiteData extends WebsocketData {
  requisite_id: string;
}
export interface WebsocketTransactionData extends WebsocketData {
  transaction_id: string;
}
export interface WebsocketWithdrawalData extends WebsocketData {
  withdrawal_id: string;
  status: string;
}
export interface WebsocketAppealData extends WebsocketData {
  appeal_id: string;
  status: string;
}
export interface WebsocketPayoutData extends WebsocketData {
  payout_id: string;
  status: string;
}

export type WebsocketMessage = {
  data: null | WebsocketData;
  message: string;
  timestamp: number;
  type: WebsocketUpdateType;
};
export type MessageCallback = (message: WebsocketMessage) => void;

class WebSocketClient {
  private static _instance: WebSocketClient | null = null;
  private _url: string;
  private _accessToken: string;
  private _socket: WebSocket | null = null;
  private _reconnectAttempts: number = 0;
  private _maxReconnectAttempts: number = 5;
  private _reconnectInterval: number = 3000; // 3 seconds
  // private _onOpenCallback?: OnOpenCallback;
  // private _onMessageCallback?: OnMessageCallback;
  // private _onErrorCallback?: OnErrorCallback;
  // private _onCloseCallback?: OnCloseCallback;
  private _isConnecting: boolean = false;
  private messageCallbacks: MessageCallback[] = [];

  private constructor(url: string, accessToken: string) {
    this._url = url;
    this._accessToken = accessToken;
  }

  // Get or create the singleton instance
  public static getInstance(url: string, accessToken: string): WebSocketClient {
    if (!WebSocketClient._instance) {
      WebSocketClient._instance = new WebSocketClient(url, accessToken);
    }
    return WebSocketClient._instance;
  }

  // Set event callbacks
  // public setCallbacks(
  //   onOpen?: OnOpenCallback,
  //   onMessage?: OnMessageCallback,
  //   onError?: OnErrorCallback,
  //   onClose?: OnCloseCallback
  // ): void {
  //   this._onOpenCallback = onOpen;
  //   this._onMessageCallback = onMessage;
  //   this._onErrorCallback = onError;
  //   this._onCloseCallback = onClose;
  // }

  // Establish the WebSocket connection
  public connect(): void {
    if (
      this._isConnecting ||
      (this._socket && this._socket.readyState === WebSocket.OPEN)
    ) {
      return;
    }
    this._isConnecting = true;

    const fullUrl = `${this._url}?accessToken=${this._accessToken}`;
    this._socket = new WebSocket(fullUrl);

    this._socket.onopen = this._onOpen.bind(this);
    this._socket.onmessage = this._onMessage.bind(this);
    this._socket.onerror = this._onError.bind(this);
    this._socket.onclose = this._onClose.bind(this);
  }

  // Handle reconnection logic
  public reconnect(): void {
    if (this._reconnectAttempts < this._maxReconnectAttempts) {
      this._reconnectAttempts++;
      setTimeout(() => {
        console.log(
          `Attempting to reconnect... (Attempt ${this._reconnectAttempts})`
        );
        this.connect();
      }, this._reconnectInterval);
    } else {
      console.log("Max reconnect attempts reached. Giving up.");
    }
  }

  // Send a message to the server
  public send(message: string): void {
    if (this._socket && this._socket.readyState === WebSocket.OPEN) {
      this._socket.send(message);
    } else {
      console.log("WebSocket is not open. Cannot send message.");
    }
  }

  // Close the connection
  public close(): void {
    if (this._socket) {
      this._socket.close();
    }
  }

  private _onOpen(/* event: Event */): void {
    console.log("WebSocket connection opened.");
    this._isConnecting = false;
    this._reconnectAttempts = 0;
    // if (this._onOpenCallback) this._onOpenCallback(event);
  }

  private _onMessage(event: MessageEvent): void {
    // if (this._onMessageCallback) {
    // this._onMessageCallback(event);
    // } else {
    console.log("Received message:", event.data);
    const msg = JSON.parse(event.data) as WebsocketMessage;
    store.dispatch(addAlert(msg.message));
    this.messageCallbacks.forEach((callback) => callback(msg));

    // this._processMessagesData(msg);
    // console.log(event.data);
    // }
  }
  
  public onMessage(callback: MessageCallback): () => void {
    this.messageCallbacks.push(callback);
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(cb => cb !== callback);
    };
  }

  private _onError(event: Event): void {
    this._isConnecting = false;
    // if (this._onErrorCallback) {
    // this._onErrorCallback(event);
    // } else {
    console.error("WebSocket error:", event);
    // }
  }

  private _onClose(event: CloseEvent): void {
    this._isConnecting = false;
    // if (this._onCloseCallback) {
    // this._onCloseCallback(event);
    // } else {
    console.log("WebSocket connection closed:", event.code, event.reason);
    // }
    if (!event.wasClean) {
      this.reconnect();
    }
  }

  // private _processMessagesData = (msg: WebsocketMessage) => {
  //   switch (msg.type) {
  //     case WebsocketUpdateType.Order: {
  //       if (msg.data == null) return;
  //       const orderData = msg.data as WebsocketOrderData;
  //       console.log(
  //         `Order ID: ${orderData.order_id}, Status: ${orderData.status}`
  //       );
  //       break;
  //     }
  //     case WebsocketUpdateType.Balance: {
  //       const balanceData = msg.data as WebsocketBalanceData;
  //       console.log(`New Balance: ${balanceData.balance}`);
  //       break;
  //     }
  //     case WebsocketUpdateType.Requisite: {
  //       const requisiteData = msg.data as { requisite_id: string };
  //       console.log(`Requisite ID: ${requisiteData.requisite_id}`);
  //       break;
  //     }
  //     case WebsocketUpdateType.Transaction: {
  //       const transactionData = msg.data as { transaction_id: string };
  //       console.log(`Transaction ID: ${transactionData.transaction_id}`);
  //       break;
  //     }
  //     case WebsocketUpdateType.Withdrawal: {
  //       const withdrawalData = msg.data as {
  //         withdrawal_id: string;
  //         status: string;
  //       };
  //       console.log(
  //         `Withdrawal ID: ${withdrawalData.withdrawal_id}, Status: ${withdrawalData.status}`
  //       );
  //       break;
  //     }
  //     case WebsocketUpdateType.Appeal: {
  //       const appealData = msg.data as { appeal_id: string; status: string };
  //       console.log(
  //         `Appeal ID: ${appealData.appeal_id}, Status: ${appealData.status}`
  //       );
  //       break;
  //     }
  //     case WebsocketUpdateType.PayOut: {
  //       const payOutData = msg.data as { payout_id: string; status: string };
  //       console.log(
  //         `PayOut ID: ${payOutData.payout_id}, Status: ${payOutData.status}`
  //       );
  //       break;
  //     }
  //     case WebsocketUpdateType.Notification:
  //     case WebsocketUpdateType.General:
  //       if (msg.data) {
  //         console.log("Data:", msg.data);
  //       }
  //       break;

  //     default:
  //       console.log("Unknown update type:", msg.type);
  //       console.log("Message:", msg.message);
  //       console.log("Data:", msg.data);
  //       break;
  //   }
  // };
}

export default WebSocketClient;
